import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NotifySignup from '../components/products/NotifySignup';
import ProductSeo from '../components/products/ProductSeo';
import StockCTA from '../components/products/StockCTA';
import WholesaleCallout from '../components/products/WholesaleCallout';
import { usePricingContext } from '../hooks/usePricingContext';
import { formatPrice } from '../utils/format';
/**
 * Generic fallback PDP rendered by ProductRoute when a slug doesn't have a
 * bespoke component registered. Minimal but fully functional: gallery, price,
 * tier table for wholesalers, and an Add-to-Cart that routes through StockCTA.
 */ export default function ProductDetailPage({ product }) {
    const [quantity, setQuantity] = useState(1);
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const pricing = usePricingContext(product);
    const galleryImages = useMemo(()=>{
        if (product.images.length > 0) {
            return product.images.slice().sort((a, b)=>a.displayOrder - b.displayOrder).map((img)=>img.imageUrl);
        }
        if (product.imageUrl) return [
            product.imageUrl
        ];
        return [];
    }, [
        product
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ProductSeo, {
                product: product
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
                children: [
                    /*#__PURE__*/ _jsxs("nav", {
                        className: "text-xs text-stone-500 mb-6",
                        children: [
                            /*#__PURE__*/ _jsx(Link, {
                                to: "/shop",
                                className: "hover:text-stone-900",
                                children: "Shop"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "mx-2",
                                children: "/"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-stone-900",
                                children: product.name
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 lg:grid-cols-2 gap-12",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "aspect-[4/5] bg-stone-100 overflow-hidden",
                                        children: galleryImages.length > 0 ? /*#__PURE__*/ _jsx("img", {
                                            src: galleryImages[activeImageIdx] ?? galleryImages[0],
                                            alt: product.name,
                                            className: "w-full h-full object-cover"
                                        }) : /*#__PURE__*/ _jsx("div", {
                                            className: "w-full h-full flex items-center justify-center text-stone-300 text-sm uppercase",
                                            children: "No image"
                                        })
                                    }),
                                    galleryImages.length > 1 && /*#__PURE__*/ _jsx("div", {
                                        className: "flex gap-2 mt-3 overflow-x-auto",
                                        children: galleryImages.map((url, idx)=>/*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>setActiveImageIdx(idx),
                                                className: `flex-shrink-0 w-16 h-16 overflow-hidden border-2 ${idx === activeImageIdx ? 'border-stone-900' : 'border-transparent'}`,
                                                children: /*#__PURE__*/ _jsx("img", {
                                                    src: url,
                                                    alt: "",
                                                    className: "w-full h-full object-cover"
                                                })
                                            }, url))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h1", {
                                        className: "font-heading text-3xl font-bold text-stone-900",
                                        children: product.name
                                    }),
                                    pricing.isInDevelopment ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-[11px] font-medium uppercase tracking-[0.28em] text-clay-600 mt-3",
                                        children: "In development"
                                    }) : /*#__PURE__*/ _jsx("p", {
                                        className: "text-2xl font-bold text-stone-900 mt-3",
                                        children: formatPrice(pricing.displayPriceCents)
                                    }),
                                    /*#__PURE__*/ _jsx(WholesaleCallout, {
                                        product: product,
                                        tone: "light"
                                    }),
                                    product.description && /*#__PURE__*/ _jsx("p", {
                                        className: "mt-5 text-stone-600 text-sm leading-relaxed whitespace-pre-line",
                                        children: product.description
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "border-t border-stone-200 my-6"
                                    }),
                                    pricing.isInDevelopment ? /*#__PURE__*/ _jsx(NotifySignup, {
                                        product: product,
                                        variant: "panel",
                                        tone: "dark",
                                        className: "mt-2"
                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs font-semibold uppercase tracking-wider text-stone-900",
                                                        children: "Quantity"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "mt-2 flex items-center gap-3",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("button", {
                                                                type: "button",
                                                                onClick: ()=>setQuantity((q)=>Math.max(1, q - 1)),
                                                                className: "w-9 h-9 border-2 border-stone-200 hover:border-stone-900 font-bold",
                                                                children: "−"
                                                            }),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "w-10 text-center text-sm font-semibold",
                                                                children: quantity
                                                            }),
                                                            /*#__PURE__*/ _jsx("button", {
                                                                type: "button",
                                                                onClick: ()=>setQuantity((q)=>q + 1),
                                                                className: "w-9 h-9 border-2 border-stone-200 hover:border-stone-900 font-bold",
                                                                children: "+"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-8",
                                                children: /*#__PURE__*/ _jsx(StockCTA, {
                                                    product: product,
                                                    quantity: quantity
                                                })
                                            })
                                        ]
                                    }),
                                    product.details && /*#__PURE__*/ _jsxs("details", {
                                        className: "mt-8",
                                        open: true,
                                        children: [
                                            /*#__PURE__*/ _jsx("summary", {
                                                className: "cursor-pointer py-3 border-b-2 border-stone-100 text-xs font-semibold text-stone-900 uppercase tracking-[0.15em]",
                                                children: "Details"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "py-4 text-sm text-stone-600 leading-relaxed",
                                                children: /*#__PURE__*/ _jsx("ul", {
                                                    className: "space-y-1.5 list-disc list-inside",
                                                    children: product.details.split('\n').filter(Boolean).map((line)=>/*#__PURE__*/ _jsx("li", {
                                                            children: line
                                                        }, line))
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
